import { inject } from "fw";
import { Store, handle } from "fw-state";
import { AlarmsRepository } from "network/alarms-repository";
import { Alarm } from "models/alarms";
import { once } from "../helpers/once";
import { StartAction } from "./actions";
import { LocalStorageCache } from "caching";

type CurrentAlarmsStoreShape = {
    currentUserId: string;
    loaded: boolean;
    lastLoaded: Date;
    list: Alarm[];
    filteredList: Alarm[];
    search: string;
    collapsedItem: string;
};

type SavedAlarmsStoreState = {
    search: string;
};

export class FetchAlarmsAction {}
export class AlarmsResetAction {}
export class AlarmsEnsureAction {}
export class AlarmsCollapseItemAction {
    constructor(public name: string) {}
}
export class AlarmsSearchAction {
    constructor(public search: string) {}
}
export class FilterAlarmsAction {
    constructor(public value: string) {}
}

@inject
export class AlarmsStore extends Store<CurrentAlarmsStoreShape> {
    constructor(
        private network: AlarmsRepository,
        private cache: LocalStorageCache
    ) {
        super();
    }

    defaultState() {
        return {
            currentUserId: null,
            loaded: true,
            lastLoaded: null,
            list: [],
            filteredList: [],
            search: null,
            collapsedItem: ""
        };
    }

    @handle(StartAction)
    private handleStartAction(action: StartAction) {
        this.setState(() => ({
            ...this.defaultState(),
            currentUserId: action.user.Id
        }));
    }

    @handle(AlarmsEnsureAction)
    private async handleAlarmsEnsureAction() {
        await once("ensure-alarms", async () => {
            this.restoreState();
        });
    }

    @handle(AlarmsCollapseItemAction)
    private async collapseItemAction(action: AlarmsCollapseItemAction) {
        this.setState((state) => ({
            ...state,
            collapsedItem: action.name
        }));
    }

    @handle(FilterAlarmsAction)
    private async filterItemList(action: FilterAlarmsAction) {
        this.searchOnList(action.value);
    }

    @handle(AlarmsResetAction)
    private async alarmsResetAction() {
        if (!this.state.loaded) return;

        this.setState(() => {
            return {
                ...this.defaultState()
            };
        });

        this.loadPageData();
    }

    @handle(AlarmsSearchAction)
    private async alarmsSearchAction(action) {
        if (!this.state.loaded) return;

        this.setState((state) => {
            return {
                ...state,
                search: action.search
            };
        });

        this.loadPageData();
    }

    @handle(FetchAlarmsAction)
    private async handleFetchAlarms() {
        this.setState(() => ({
            ...this.state,
            loaded: false
        }));

        await this.loadPageData();
    }

    private async loadPageData() {
        try {
            const clientModelResponse = await this.network.list();
            this.setState((state) => ({
                ...state,
                loaded: true,
                lastLoaded: new Date(),
                list: clientModelResponse
            }));

            if (this.state.search) this.searchOnList(this.state.search);

            this.saveState();
        } catch (err) {
            this.setState((state) => ({ ...state, loaded: true, errorLoading: true }));
        }
    }

    get cacheKey(): string {
        return `manager-user:${this.state.currentUserId}:ats-alarms-store`;
    }

    private restoreState() {
        if (this.state.currentUserId == null) return;

        const savedState = this.cache?.get<SavedAlarmsStoreState>(this.cacheKey);

        if (savedState) {
            this.setState((state) => ({
                ...state,
                search: savedState.search
            }));
        }

        this.loadPageData();
        this.searchOnList(savedState?.search);
    }

    private saveState() {
        if (this.state.currentUserId === null) return;

        const { currentUserId, loaded, list, ...rest } = this.state;

        this.cache?.set<SavedAlarmsStoreState>(this.cacheKey, { ...rest });
    }

    private searchOnList(value) {
        this.setState((state) => {
            return {
                ...state,
                filteredList: state.list.filter((i) => i.Name.includes(value))
            };
        });
    }
}
